const Utils = require('../utils');
const AdserverBase = require('../adserverBase');
const DivSlot = require('../divSlot');

const gamSlotOf = (slot) => slot?.getGamSlot?.() || slot;

class GamDelayedSlot extends DivSlot {
	getGamSlot() {
		return Utils.find(window.googletag?.pubads?.()?.getSlots?.() || [], (gamSlot) => (
			gamSlot.getSlotElementId() === this.getSlotElementId()
		));
	}

	waitGamSlot(cb) {
		Utils.withGoogle(() => {
			cb(this.getGamSlot());
		});
	}

	setTargeting(...args) {
		this.waitGamSlot((slot) => slot.setTargeting(...args));
	}
}

class GamAdserver extends AdserverBase {
	init(__, doneCb) {
		const { noGpt } = this.auction;
		if (!noGpt && !GamAdserver.gptLoaded) {
			GamAdserver.gptLoaded = true;
			Utils.loadScript('//securepubads.g.doubleclick.net/tag/js/gpt.js');
		}
		this.maybeWaitGoogle(doneCb);
	}

	doFirstTimeInit() {
		this.maybeWaitGoogle(() => {
			this.gamCall(this.googletag.pubads(), 'addEventListener', 'slotRenderEnded', ({ slot }) => {
				this.auction.pbRequester.registerRenderedDivId(
					slot.getSlotElementId(),
					slot.getTargeting('hb_adid')?.[0] || null,
				);
			});
		}, true);
	}

	maybeWaitGoogle(cb, forceWait) {
		if (this.auction.delayedAdserverLoading && !forceWait) {
			cb();
		} else {
			Utils.withGoogle((googletag) => {
				this.googletag = googletag;
				cb();
			});
		}
	}

	static destroySlots() {
		window.googletag?.destroySlots?.();
	}

	waitRender(cb) {
		const listener = (ev) => {
			if (ev.rlvStopPropagation) {
				return;
			}
			const doContinue = cb({
				...ev,
				orderId: ev.campaignId,
				stopPropagation: () => {
					ev.rlvStopPropagation = true;
				},
			});
			if (!doContinue) {
				this.gamCall(this.googletag.pubads(), 'removeEventListener', 'slotRenderEnded', listener);
			}
		};
		this.maybeWaitGoogle(() => {
			this.gamCall(this.googletag.pubads(), 'addEventListener', 'slotRenderEnded', listener);
		}, true);
		return true;
	}

	adUnitInit({ adUnit }) {
		adUnit.setAdUnitPaths = function (paths) {
			this.gamPath = paths.join('\n');
		};
	}

	gamCall(gamObj, fnName, ...args) {
		const fn = this.auction.googletagCalls?.[fnName];
		return fn ? fn.call({ auction: this.auction }, ...args) : gamObj[fnName](...args);
	}

	getSlotResponseInfo({ slot }) {
		const info = gamSlotOf(slot)?.getResponseInformation();
		return !info ? null : {
			advertiserId: info.advertiserId,
			orderId: info.campaignId,
		};
	}

	normalizeAdUnitPath(path) {
		return Utils.normalize(path);
	}

	adUnitPathsFromUnitInternal(unit) {
		return Utils.normalizedPaths(unit.gamPath, true);
	}

	updateAdUnitPath(dstAdUnit, adUnitPath) {
		dstAdUnit.gamPath = adUnitPath;
		return true;
	}

	getCodeStart(path) {
		return `/${path}`;
	}

	getType() {
		return 'google';
	}

	getAdserverProps() {
		return Utils.assign(super.getAdserverProps(), {
			delayedSendAdserver: true,
		});
	}

	getSlots() {
		if (this.auction.delayedAdserverLoading) {
			return GamDelayedSlot.list();
		}
		if (!this.googletag?.pubads) {
			return [];
		}
		return this.gamCall(this.googletag.pubads(), 'getSlots');
	}

	getAmazonIntegrationInfo() {
		return {
			adServerName: 'googletag', // config.adServer
			useSetDisplayBids: true,
		};
	}

	rlvConvertedAdUnitPath(slot) {
		return Utils.rlvConvertedGamAdUnitPath(slot, this);
	}

	createSlot({
		path, sizes, divId, placementType,
	}) {
		const { pbRequester } = this.auction;
		let finalPath = Utils.injectMcmPart(path, pbRequester.mcmChildNwid);
		const { customDimStr } = this;
		let customDimVal;
		if (customDimStr) {
			const idx = finalPath.lastIndexOf('/');
			if (idx > 0) {
				customDimVal = finalPath.substring(idx + 1);
				finalPath = finalPath.substring(0, idx);
			}
		}
		const szs = placementType.makeFluid ? [...sizes, 'fluid'] : sizes;
		const createGamSlot = () => {
			const gamSlot = this.gamCall(this.googletag, 'defineSlot', finalPath, szs, divId);
			if (gamSlot) {
				gamSlot.addService(this.googletag.pubads());
				if (customDimVal) {
					gamSlot.setTargeting(customDimStr, customDimVal);
				}
			}
			return gamSlot;
		};
		let slot;
		if (this.auction.delayedAdserverLoading) {
			slot = GamDelayedSlot.getOrCreateSlot(divId, finalPath);
			this.maybeWaitGoogle(createGamSlot, true);
		} else {
			slot = createGamSlot();
		}
		return slot;
	}

	oneTimePageSetup() {
		this.maybeWaitGoogle(() => {
			const { googletag } = this;
			this.gamCall(googletag.pubads(), 'disableInitialLoad');
			this.gamCall(googletag.pubads(), 'enableSingleRequest');
			this.gamCall(googletag, 'enableServices');
		}, true);
	}

	sendAdserverRequest(params) {
		this.maybeWaitGoogle(() => {
			this.sendAdserverRequestInternal(params);
			params.onRequestSent();
		}, true);
	}

	setupLazyLoading() {
		const { googletag } = this;
		const { enabled, ...settings } = this.auction.data.rlvGamLazyLoad;

		if (!enabled) {
			return;
		}

		Utils.entries(settings).forEach(([key, value]) => {
			if (value == null) {
				delete settings[key];
			}
		});
		this.gamCall(googletag.pubads(), 'enableLazyLoad', settings);
	}

	sendAdserverRequestInternal({ isFirstCall, requestAuction, unknownSlotsToLoad }) {
		const {
			pbjs, isReloadAuction, unitDataRemoved,
			pbRequester, noAdsInitRequestAll,
		} = requestAuction;
		const usedUnitDatas = requestAuction.unitDatasByAds(this);
		const { googletag } = this;

		this.setupLazyLoading();

		if (pbjs.setTargetingForGPTAsync) { // We're not 100% sure as pbjs-loading might have failed
			const codes = unitDataRemoved ? usedUnitDatas.map((u) => u.code) : undefined;
			requestAuction.pbjsCall('setTargetingForGPTAsync', codes, (slot) => (adUnitCode) => {
				const id = AdserverBase.codeToId[adUnitCode];
				if (id) {
					return id === slot.getSlotElementId();
				}
				return Utils.cleanMcmPart(slot.getAdUnitPath()) === Utils.cleanMcmPart(adUnitCode)
					|| slot.getSlotElementId() === adUnitCode;
			});
		}
		// Force banner adserver targeting if option is set
		usedUnitDatas.forEach(({ adUnit, pbAdUnit }) => {
			const { videoSettings } = adUnit;
			if (videoSettings
				&& (videoSettings.adserverTargetingOptions || {}).forceBannerAdserverTargeting) {
				const { __slot: s } = pbAdUnit;
				const slot = gamSlotOf(s);
				const targeting = slot.getTargeting('hb_format');
				if (targeting.indexOf('video') !== -1 && targeting.indexOf('banner') === -1) {
					slot.setTargeting('hb_format', 'banner');
				}
			}
		});
		let refreshSlots;
		const allSlots = this.getSlots();

		const globalTargeting = this.getGlobalTargeting();
		Utils.entries(globalTargeting).forEach((entry) => {
			this.gamCall(
				googletag.pubads(),
				'setTargeting',
				...entry,
			);
		});

		if (!isFirstCall || noAdsInitRequestAll) {
			refreshSlots = usedUnitDatas.map(({ slot }) => {
				if (isReloadAuction) {
					const gamSlot = gamSlotOf(slot);
					if (gamSlot?.getResponseInformation()) {
						gamSlot.setCollapseEmptyDiv(false, false); // don't collapse when reloading and there was an ad
					}
				}
				return slot;
			});
			refreshSlots.push(...unknownSlotsToLoad);
		} else {
			refreshSlots = allSlots.filter((slot) => (
				(!slot.creatorPbAuction || slot.creatorPbAuction === requestAuction)
				&& !pbRequester.lazyLoader.isLazyLoadingSlot(requestAuction, slot)
			));
			if (allSlots.length === refreshSlots.length) {
				refreshSlots = null;
			}
		}
		if (refreshSlots) {
			const arr = refreshSlots.filter((slot) => !slot.noRefresh);
			const seenSlots = new Set(); // To support "shared" slots
			const gamSlotArr = arr.map(gamSlotOf).filter((s) => {
				if (!s || seenSlots.has(s)) {
					return false;
				}
				seenSlots.add(s);
				return true;
			});
			if (gamSlotArr.length) {
				this.gamCall(googletag.pubads(), 'refresh', gamSlotArr);
			}
		} else if (allSlots.length) {
			this.gamCall(googletag.pubads(), 'refresh');
		}
	}
}

module.exports = GamAdserver;
